/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import axios from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { decodeJWT } from 'utils/jwtDecode';
import { useAPI, Event } from 'components/lib';
import { capture, identifyUser, resetUser } from '../analytics/posthog';

// auth context
export const AuthContext = createContext();
const permissions = require('./permissions');

const getQuery = (variable) => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) return pair[1];
  }
  return false;
};

export function AuthProvider(props) {
  const cache = JSON.parse(localStorage.getItem('user'));
  const userData = decodeJWT(cache?.token)
  const [user, setUser] = useState(userData);
  const auth = useAPI(user ? '/api/auth' : null);
  const [plan, setPlan] = useState(null);
  const [activePlan, setActivePlan] = useState(null);

  useEffect(() => {
    // update the auth status
    if (!auth.loading) {
      if (auth.error && auth.error.response && auth.error.response.status === 401) {
        signout();
      } else if (auth.data) {
        if (!auth.data.authenticated) {
          signout();
        } else if (auth.data.token) {
          const newCache = JSON.parse(localStorage.getItem('user'));
          localStorage.setItem('user', JSON.stringify({ ...newCache, token: auth.data.token }))
          const newUserData = decodeJWT(auth.data.token)
          if (newUserData) setUser(newUserData)
        }
      }
    }
  }, [auth]);

  const checkIntegrationInstall = () => {
    const code = getQuery('code');
    const state = getQuery('state');
    if (code && state === 'connect-pipedrive') {
      window.sessionStorage.setItem('code', code);
      window.sessionStorage.setItem('integration', 'pipedrive');
    }
  };

  // Helper to add small delay before redirect to ensure DB writes complete
  const safeRedirect = (url) => {
    setTimeout(() => { window.location = url; }, 500);
  };

  function signin(res) {
    if (res.data) {
      localStorage.setItem('user', JSON.stringify(res.data));
      const userInfo = decodeJWT(res.data.token);
      axios.defaults.headers.common['Authorization'] =
        'Bearer ' + res.data.token;
      Event('signin');
      identifyUser(userInfo);
      capture('user_logged_in', {
        permission: userInfo?.permission,
        account_type: userInfo?.account_type,
      });
      if (userInfo?.newSignUp) {
        capture('user_signed_up', {
          account_type: userInfo?.account_type,
          signup_method: res.data?.signup_method || 'email',
        });
      }

      if (userInfo) {
        // Check for a pending post-auth redirect (set by magic link, etc.)
        const postAuthRedirect = window.sessionStorage.getItem('postAuthRedirect');
        if (postAuthRedirect) {
          window.sessionStorage.removeItem('postAuthRedirect');
          try {
            const url = new URL(postAuthRedirect, window.location.origin);
            // Only allow same-origin redirects
            if (url.origin === window.location.origin) {
              return safeRedirect(url.pathname + url.search + url.hash);
            }
          } catch (e) {
            // Invalid URL, fall through to default logic
          }
        }

        if (userInfo.account_type === 'agent' && userInfo.newSignUp) {
          return safeRedirect('/account/integration/api');
        }
        if (userInfo.integrationExist)
          return safeRedirect(`/account/integration/${userInfo.integration}`);
        if (userInfo.ltd) return safeRedirect('/account/billing');
        if (userInfo.newUser) {
          localStorage.setItem('newUser', 'required');
          if (!userInfo.plan || userInfo.plan === 'free')
            return safeRedirect('/account/billing');
          if(!userInfo.phone) {
            sessionStorage.setItem('phone', 'required')
            if (userInfo.plan === "Trial") {
              sessionStorage.setItem('onboarding', 'required')
              return safeRedirect('/onboarding');
            } else return safeRedirect('/dashboard');
          }
          if (userInfo.plan === "Trial" && userInfo.newSignUp) {
            sessionStorage.setItem('onboarding', 'required')
            return safeRedirect('/onboarding');
          }
          return safeRedirect('/dashboard');
        } else return safeRedirect('/dashboard');
      }
    }
  }

  async function signout() {
    try { 
      localStorage.clear();
      setUser(null);
      resetUser();
      await axios({ method: 'delete', url: '/api/auth' }); 
    } catch (e) {}
    if (window.Beacon) {
      window.Beacon("logout");
    }
    window.location.href = '/signin';
  }

  async function switchAccount(id) {
    const res = await axios({
      method: 'post',
      url: '/api/auth/switch',
      data: { account: id },
    });

    if (res.data) signin(res);
  }

  function update(data) {
    if (localStorage.getItem('user') && user) {
      setUser((prevUser) => ({ ...prevUser, ...data }))
      // let userInfo = JSON.parse(localStorage.getItem('user'));
      // for (let key in data) {
      //   if (Array.isArray(data[key])) {
      //     userInfo[key] = data[key];
      //   } else if (typeof data[key] === 'object') {
      //     for (let innerKey in data[key]) {
      //       userInfo[key][innerKey] = data[key][innerKey];
      //     }
      //   } else {
      //     userInfo[key] = data[key];
      //   }
      // }

      // console.trace("TEST update ", window.location, userInfo)

      // localStorage.setItem('user', JSON.stringify(userInfo));
      // const userData = decodeJWT(userInfo?.token)
      // if (userData) setUser(userData);
    }
  }

  useEffect(() => {
    if (!auth.loading) {
      getUseOnboarding();
      getActivePlanDetails();
    }
  }, [auth]);

  const getUseOnboarding = async () => {
    try {
      if(window.location.pathname !== '/signin'){
          const res = await axios({
            url: '/api/active-plan',
            method: 'get',
          });
          setPlan(res.data.data);
        }
    } catch (err) {
      console.log('ERROR ', err.response);
    }
  };

  const getActivePlanDetails = async () => {
    try {
      const res = await axios({
        url: '/api/account/plan',
        method: 'get',
      });
      setActivePlan(res.data.data);
    } catch (err) {
      console.log('ERROR getting active plan', err.response);
    }
  };

  checkIntegrationInstall(window.location.search);

  return (
    <AuthContext.Provider
      value={{
        user: user,
        signin: signin,
        signout: signout,
        update: update,
        switchAccount: switchAccount,
        permission: permissions[user?.permission],
        plan,
        activePlan,
        getActivePlanDetails
      }}
      {...props}
    />
  );
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props) {
  // check user exists
  const authContext = useContext(AuthContext);
  const userData = authContext?.user;
  const path = window.location.pathname;

  if (userData) {
    if (permissions[userData.permission][props.permission]) {
      // user has no plan
      if (!userData.plan && path !== '/account/profile' && path !== '/account/billing' && path !== '/verification')
        return <Navigate to="/account/billing" />;

      // user is good
      return props.children;
    }
    if (userData.permission === 'user') {
      if(userData.expired) localStorage.setItem('expiredPlan', userData.permission);
      else localStorage.setItem('noPermission', userData.permission);
      return <Navigate to="/" />;
    }
  }

  // user is not authenticated
  return <Navigate to="/signin" />;
}
